import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
    { to: "/", label: "Home" },
    { to: "/collection", label: "Collection" },
    { to: "/about", label: "About" },
    { to: "/#appointment", label: "Book a Visit" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();
    const isHome = pathname === "/";

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [pathname]);

    // When on Home and near the top, show the decorative centered crest header.
    const showCrest = isHome && !scrolled;

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
                scrolled
                    ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            {/* ------------------ HOME · DECORATIVE CREST HEADER ------------------ */}
            <AnimatePresence>
                {showCrest && (
                    <motion.div
                        key="crest"
                        initial={{ opacity: 0, y: -16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -16 }}
                        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                        className="px-6 pt-4 lg:pt-5 pb-1 flex flex-col items-center text-black"
                    >
                        <Link
                            to="/"
                            data-testid="nav-crest"
                            className="flex flex-col items-center group"
                            aria-label="Hoofs and Hides — Home"
                        >
                            <motion.img
                                src="/brand/logo-mark-black.png"
                                alt=""
                                className="h-10 sm:h-12 lg:h-14 w-auto select-none pointer-events-none"
                                draggable="false"
                                animate={{ y: [0, -2, 0] }}
                                transition={{
                                    duration: 6,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                }}
                            />
                            <div className="mt-2 flex flex-col items-center">
                                <span
                                    data-testid="nav-crest-title"
                                    className="font-medium leading-none text-[15px] sm:text-[17px] lg:text-[19px] text-black"
                                    style={{
                                        fontFamily:
                                            "Playfair Display, ui-sans-serif, system-ui, sans-serif",
                                        letterSpacing: "0.42em",
                                    }}
                                >
                                    HOOFS &amp; HIDES
                                </span>
                                <span
                                    data-testid="nav-crest-subtitle"
                                    className="mt-1.5 block text-[8px] sm:text-[9px] uppercase text-black/75"
                                    style={{
                                        fontFamily:
                                            "Montserrat, ui-sans-serif, system-ui, sans-serif",
                                        fontWeight: 500,
                                        letterSpacing: "1.08em",
                                    }}
                                >
                                    LEATHER DIVISION
                                </span>
                            </div>
                        </Link>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ------------------ NAVIGATION ROW ------------------ */}
            <div
                className={`px-6 sm:px-10 lg:px-16 ${
                    showCrest ? "h-11 mt-0" : "h-20"
                } flex items-center justify-between transition-all duration-500`}
            >
                {/* LEFT slot: logo (hidden on Home crest mode), else brand signature */}
                <div className="flex-1 flex items-center">
                    {!showCrest && (
                        <Link
                            to="/"
                            data-testid="nav-logo"
                            className="flex items-center"
                            aria-label="Hoofs and Hides — Home"
                        >
                            <img
                                src="/brand/round_white_logo.png"
                                alt="Hoofs & Hides"
                                className="h-8 sm:h-9 w-auto select-none pointer-events-none"
                                draggable="false"
                            />
                        </Link>
                    )}
                    {showCrest && (
                        <span className="hidden lg:block text-[10px] uppercase tracking-[0.32em] text-white/50 font-mono-custom">
                            Est. 1992 · Bengaluru
                        </span>
                    )}
                </div>

                {/* CENTER nav links */}
                <nav
                    className={`hidden lg:flex items-center gap-10 ${
                        showCrest ? "" : ""
                    }`}
                >
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `text-[11px] uppercase tracking-[0.24em] font-medium transition-colors hover-underline ${
                                    isActive && l.to !== "/#appointment"
                                        ? "text-white"
                                        : "text-white/70 hover:text-white"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                {/* RIGHT slot */}
                <div className="flex-1 flex justify-end items-center">
                    <div className="hidden lg:block">
                        <Link
                            to="/#appointment"
                            data-testid="nav-cta-book"
                            className="group inline-flex items-center gap-3 border border-white/30 px-6 py-3 text-[11px] uppercase tracking-[0.24em] hover:bg-white hover:text-black transition-all duration-500"
                        >
                            <span>Book Appointment</span>
                            <span className="w-6 h-px bg-current transition-all duration-500 group-hover:w-10" />
                        </Link>
                    </div>

                    <button
                        data-testid="nav-mobile-toggle"
                        onClick={() => setOpen(!open)}
                        className="lg:hidden text-white"
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-t border-white/10"
                    >
                        <div className="px-6 py-8 flex flex-col gap-6">
                            {links.map((l) => (
                                <Link
                                    key={l.to}
                                    to={l.to}
                                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="font-serif text-3xl text-white/90 hover:text-white transition-colors"
                                >
                                    {l.label}
                                </Link>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
